import React, { useState } from 'react';
import { Card } from '../ui/card.js';
import { useNotificationSettings } from '../../stores/useSettingsStore.js';
import { Mail, MessageSquare, Monitor, Bell, Phone } from 'lucide-react';
import toast from 'react-hot-toast';

const NotificationsTab: React.FC = () => {
  const { notifications, setNotifications } = useNotificationSettings();
  const [loading, setLoading] = useState(false);

  const toggleNotification = async (type: keyof typeof notifications) => {
    setLoading(true);
    try {
      setNotifications({ [type]: !notifications[type] });
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} notifications ${!notifications[type] ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating notification settings:', error);
      toast.error('Failed to update notification settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Notification Settings</h3>
        <p className="text-sm text-slate-400 mb-6">
          Configure how and when you receive notifications from Beehive.
        </p>
      </div>

      {/* Email Notifications */}
      <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Mail className="w-5 h-5 text-blue-400" />
          <h4 className="text-base font-medium cream-gradient-text text-drop-shadow">Email Notifications</h4>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-300">Email alerts</p>
            <p className="text-xs text-slate-500">
              Receive email notifications for task completions and important updates
            </p>
          </div>
          <button
            onClick={() => toggleNotification('email')}
            disabled={loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              notifications.email ? 'bg-blue-600' : 'bg-slate-600'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                notifications.email ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </Card>

      {/* Slack Notifications */}
      <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <MessageSquare className="w-5 h-5 text-green-400" />
          <h4 className="text-base font-medium cream-gradient-text text-drop-shadow">Slack Integration</h4>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-300">Slack notifications</p>
            <p className="text-xs text-slate-500">
              Send notifications to your Slack workspace when tasks complete
            </p>
          </div>
          <button
            onClick={() => toggleNotification('slack')}
            disabled={loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              notifications.slack ? 'bg-green-600' : 'bg-slate-600'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                notifications.slack ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {notifications.slack && (
          <div className="mt-4 p-4 bg-slate-700 rounded-lg">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Slack Webhook URL
            </label>
            <input
              type="url"
              placeholder="https://hooks.slack.com/services/..."
              className="w-full p-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
            />
            <p className="text-xs text-slate-500 mt-1">
              Enter your Slack webhook URL to receive notifications
            </p>
          </div>
        )}
      </Card>

      {/* SMS Notifications */}
      <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Phone className="w-5 h-5 text-orange-400" />
          <h4 className="text-base font-medium cream-gradient-text text-drop-shadow">SMS Notifications</h4>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-300">Text message alerts</p>
            <p className="text-xs text-slate-500">
              Receive SMS notifications for critical CI/CD events and build failures
            </p>
          </div>
          <button
            onClick={() => toggleNotification('sms')}
            disabled={loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              notifications.sms ? 'bg-orange-600' : 'bg-slate-600'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                notifications.sms ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {notifications.sms && (
          <div className="mt-4 p-4 bg-slate-700 rounded-lg">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              placeholder="+1 (555) 123-4567"
              className="w-full p-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
            />
            <p className="text-xs text-slate-500 mt-1">
              Enter your phone number to receive SMS notifications for build failures and critical events
            </p>
          </div>
        )}
      </Card>

      {/* Desktop Notifications */}
      <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Monitor className="w-5 h-5 text-purple-400" />
          <h4 className="text-base font-medium cream-gradient-text text-drop-shadow">Desktop Notifications</h4>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-300">Browser notifications</p>
            <p className="text-xs text-slate-500">
              Show desktop notifications in your browser for real-time updates
            </p>
          </div>
          <button
            onClick={() => toggleNotification('desktop')}
            disabled={loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              notifications.desktop ? 'bg-purple-600' : 'bg-slate-600'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                notifications.desktop ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {notifications.desktop && (
          <div className="mt-4 p-4 bg-slate-700 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-slate-300 mb-4">
              <Bell className="w-4 h-4" />
              <span>Desktop notifications are enabled</span>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Make sure to allow notifications in your browser settings
            </p>
            
            <div className="pl-6 border-l-2 border-purple-500/30">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Desktop Notification Types</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm text-slate-300">🔔 Notify on task complete</label>
                    <p className="text-xs text-slate-500">Get notified when tasks finish successfully</p>
                  </div>
                  <button
                    onClick={() => setNotifications({ 
                      taskComplete: !notifications.taskComplete 
                    })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      notifications.taskComplete ? 'bg-purple-600' : 'bg-slate-600'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      notifications.taskComplete ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm text-slate-300">🔔 Notify on input required</label>
                    <p className="text-xs text-slate-500">Get notified when Beehive needs your input</p>
                  </div>
                  <button
                    onClick={() => setNotifications({ 
                      requiresInput: !notifications.requiresInput 
                    })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      notifications.requiresInput ? 'bg-purple-600' : 'bg-slate-600'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      notifications.requiresInput ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default NotificationsTab;
